import { styled, Switch, SwitchProps, Theme } from "@mui/material";
import React from "react";

export interface ToggleButtonProps {
  checked: boolean;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  size?: "small" | "medium" | "large";
  color?: SwitchProps["color"];
  disabled?: boolean;
}

const getSizeConfig = (theme: Theme, size: "small" | "medium" | "large") => {
  switch (size) {
    case "small":
      return {
        width: 35,
        height: 20,
        thumbSize: 16,
        margin: 2,
        translateX: 15,
      };
    case "medium":
      return {
        width: 42,
        height: 24,
        thumbSize: 20,
        margin: 2,
        translateX: 18,
      };
    case "large":
      return {
        width: 56,
        height: 32,
        thumbSize: 28,
        margin: 2,
        translateX: 24,
      };
    default:
      return getSizeConfig(theme, "small");
  }
};

const CustomToggle = styled(Switch, {
  shouldForwardProp: (prop) => prop !== "toggleSize",
})<{ toggleSize: "small" | "medium" | "large" }>(({ theme, toggleSize }) => {
  const sizeConfig = getSizeConfig(theme, toggleSize);

  return {
    width: sizeConfig.width,
    height: sizeConfig.height,
    padding: 0,
    display: "inline-flex",
    "& .MuiSwitch-switchBase": {
      padding: sizeConfig.margin,
      transitionDuration: theme.transitions.duration.shorter,
      "&.Mui-checked": {
        transform: `translateX(${sizeConfig.translateX}px)`,
        color: "#fff",
        "& + .MuiSwitch-track": {
          backgroundColor:
            theme.nawy?.colors?.semantic?.layer?.accent ||
            theme.palette.primary.main,
          opacity: 1,
        },
        "&.Mui-disabled": {
          color: theme.palette.action.disabled,
          "& + .MuiSwitch-track": {
            opacity: 0.6,
            cursor: "not-allowed",
            backgroundColor:
              theme.nawy?.colors?.semantic?.layer?.extension?.disabled ||
              theme.palette.action.disabled,
          },
        },
      },
      "&.Mui-disabled": {
        color: theme.palette.action.disabled,
        "& + .MuiSwitch-track": {
          opacity: 0.6,
          cursor: "not-allowed",
          backgroundColor:
            theme.nawy?.colors?.semantic?.layer?.extension?.disabled ||
            theme.palette.action.disabled,
        },
      },
      "&.Mui-focusVisible .MuiSwitch-thumb": {
        color: theme.nawy?.colors?.semantic?.text?.onColor || "#fff",
        border: `6px solid ${theme.palette.common.white}`,
      },
    },
    "& .MuiSwitch-thumb": {
      boxSizing: "border-box",
      width: sizeConfig.thumbSize,
      height: sizeConfig.thumbSize,
      backgroundColor: theme.nawy?.colors?.semantic?.text?.onColor || "#fff",
      boxShadow: theme.shadows[1],
    },
    "& .MuiSwitch-track": {
      borderRadius: sizeConfig.height / 2,
      backgroundColor:
        theme.nawy?.colors?.semantic?.layer?.secondaryDark ||
        theme.palette.grey[300],
      opacity: 1,
      transition: theme.transitions.create(["background-color"], {
        duration: theme.transitions.duration.standard,
      }),
    },
    "&.Mui-disabled": {
      opacity: 0.6,
      cursor: "not-allowed",
    },
  };
});

const ToggleButton: React.FC<ToggleButtonProps> = ({
  checked,
  onChange,
  size = "small",
  color = "primary",
  disabled = false,
}) => {
  return (
    <CustomToggle
      checked={checked}
      onChange={onChange}
      toggleSize={size}
      color={color}
      disabled={disabled}
    />
  );
};

export default ToggleButton;
